import { useQuery } from '@tanstack/react-query'
import { api } from '../api'

export interface ReferenceItem {
  id: number
  name: string
  isDisabledByPlatform: boolean
}

export interface ReferenceCountry extends ReferenceItem {
  isoCode2: string
  isoCode3: string
}

export interface ReferenceIntegration extends ReferenceItem {
  integrationType: string
}

export interface ReferenceCausal extends ReferenceItem {
  isDeposit: boolean
  isWithdrawal: boolean
}

// Named as a hook because it is one: every caller invokes it unconditionally.
const useLookup = <T,>(path: string, key: string) =>
  useQuery({
    queryKey: ['reference', path],
    queryFn: async (): Promise<T[]> => {
      const res = await api.get(`/reference/${path}`)
      return ((res.data as Record<string, T[]>)[key] ?? []) as T[]
    },
    staleTime: 10 * 60 * 1000,
  })

/**
 * useReference loads the read-only lookups the PSP pages point at: payment
 * method types, movement types, countries, wallet causals and setting types.
 *
 * They are small (9 to 454 rows), served whole and cached for the session, so a
 * picker never paginates and a name is always resolvable client-side.
 */
export function useReference() {
  const types = useLookup<ReferenceItem>('payment-method-types', 'paymentMethodTypes')
  const movements = useLookup<ReferenceItem>('movement-types', 'movementTypes')
  const countries = useLookup<ReferenceCountry>('countries', 'countries')
  const causals = useLookup<ReferenceCausal>('causals', 'causals')
  const settingTypes = useLookup<ReferenceItem>('setting-types', 'settingTypes')
  const networkTypes = useLookup<ReferenceItem>('network-types', 'networkTypes')

  return {
    paymentMethodTypes: types.data ?? [],
    movementTypes: movements.data ?? [],
    countries: countries.data ?? [],
    causals: causals.data ?? [],
    settingTypes: settingTypes.data ?? [],
    networkTypes: networkTypes.data ?? [],
    isLoading:
      types.isLoading ||
      movements.isLoading ||
      countries.isLoading ||
      causals.isLoading ||
      settingTypes.isLoading ||
      networkTypes.isLoading,
  }
}

/**
 * useFinanceReference loads what a causal points at: its family, its group, the
 * casino transaction type and the game type.
 *
 * The first three are lookups under /reference; game types are a catalogue of
 * this backoffice, so they come from their own endpoint — asked for whole
 * (retired ones included), because a causal may legitimately still point at a
 * type that is no longer offered.
 */
export function useFinanceReference() {
  const families = useLookup<ReferenceItem>('causal-families', 'causalFamilies')
  const groups = useLookup<ReferenceItem>('causal-groups', 'causalGroups')
  const transactionTypes = useLookup<ReferenceItem>('transaction-types', 'transactionTypes')

  const gameTypes = useQuery({
    queryKey: ['reference', 'game-types'],
    queryFn: async (): Promise<ReferenceItem[]> => {
      const res = await api.get('/game-types', { params: { includeDisabled: 'true', limit: 200 } })
      const rows = (res.data as { gameTypes?: GameTypeRow[] }).gameTypes ?? []
      return rows.map((g) => ({
        id: g.gameTypeId,
        name: g.gameType,
        isDisabledByPlatform: g.isDisabledByPlatform,
      }))
    },
    staleTime: 10 * 60 * 1000,
  })

  return {
    causalFamilies: families.data ?? [],
    causalGroups: groups.data ?? [],
    transactionTypes: transactionTypes.data ?? [],
    gameTypes: gameTypes.data ?? [],
    isLoading:
      families.isLoading ||
      groups.isLoading ||
      transactionTypes.isLoading ||
      gameTypes.isLoading,
  }
}

/**
 * useGamesReference loads what the games page filters by: the 128 brands that
 * actually publish games, the game types, and the sub-types with the type each
 * one belongs to (so the form can narrow the second picker to the first).
 *
 * All three are small and fetched whole — a filter that paginates is a filter
 * nobody uses.
 */
export function useGamesReference() {
  const brands = useQuery({
    queryKey: ['reference', 'brands'],
    queryFn: async (): Promise<ReferenceItem[]> => {
      const res = await api.get('/brands', { params: { includeDisabled: 'true', limit: 500 } })
      const rows = (res.data as { brands?: BrandRow[] }).brands ?? []
      return rows.map((b) => ({
        id: b.brandId,
        name: b.brand,
        isDisabledByPlatform: b.isDisabledByPlatform,
      }))
    },
    staleTime: 10 * 60 * 1000,
  })

  const gameTypes = useQuery({
    queryKey: ['reference', 'game-types'],
    queryFn: async (): Promise<ReferenceItem[]> => {
      const res = await api.get('/game-types', { params: { includeDisabled: 'true', limit: 200 } })
      const rows = (res.data as { gameTypes?: GameTypeRow[] }).gameTypes ?? []
      return rows.map((g) => ({
        id: g.gameTypeId,
        name: g.gameType,
        isDisabledByPlatform: g.isDisabledByPlatform,
      }))
    },
    staleTime: 10 * 60 * 1000,
  })

  const subTypes = useQuery({
    queryKey: ['reference', 'game-subtypes'],
    queryFn: async (): Promise<GameSubTypeItem[]> => {
      const res = await api.get('/game-subtypes', {
        params: { includeDisabled: 'true', limit: 500 },
      })
      const rows = (res.data as { gameSubTypes?: GameSubTypeRow[] }).gameSubTypes ?? []
      return rows.map((s) => ({
        id: s.gameSubTypeId,
        name: s.gameSubType,
        gameTypeId: s.gameTypeId,
        isDisabledByPlatform: s.isDisabledByPlatform,
      }))
    },
    staleTime: 10 * 60 * 1000,
  })

  return {
    brands: brands.data ?? [],
    gameTypes: gameTypes.data ?? [],
    gameSubTypes: subTypes.data ?? [],
    isLoading: brands.isLoading || gameTypes.isLoading || subTypes.isLoading,
  }
}

export interface GameSubTypeItem extends ReferenceItem {
  gameTypeId: number
}

interface BrandRow {
  brandId: number
  brand: string
  isDisabledByPlatform: boolean
}

interface GameSubTypeRow {
  gameSubTypeId: number
  gameSubType: string
  gameTypeId: number
  isDisabledByPlatform: boolean
}

interface GameTypeRow {
  gameTypeId: number
  gameType: string
  isDisabledByPlatform: boolean
}

/**
 * useIntegrations loads the platform integrations — the picker behind a bank's
 * integration links.
 *
 * It reads the editable catalogue (/integrations) rather than a /reference
 * lookup: that table is now maintained in this backoffice, and /reference is for
 * the ones it does not touch. Retired integrations are included so an existing
 * pairing stays nameable.
 */
export function useIntegrations() {
  const query = useQuery({
    queryKey: ['reference', 'integrations'],
    queryFn: async (): Promise<ReferenceIntegration[]> => {
      const res = await api.get('/integrations', { params: { includeDisabled: 'true', limit: 200 } })
      const rows = (res.data as { integrations?: IntegrationRow[] }).integrations ?? []
      return rows.map((i) => ({
        id: i.integrationId,
        name: i.integration,
        integrationType: i.integrationType,
        isDisabledByPlatform: i.isDisabledByPlatform,
      }))
    },
    staleTime: 10 * 60 * 1000,
  })
  return { integrations: query.data ?? [], isLoading: query.isLoading }
}

interface IntegrationRow {
  integrationId: number
  integration: string
  integrationType: string
  isDisabledByPlatform: boolean
}

/**
 * useSettingTemplates loads the four rules-engine templates as a picker list.
 *
 * Retired ones are included: a group may legitimately still sit under a template
 * that is no longer offered, and the page has to name it.
 */
export function useSettingTemplates() {
  const query = useQuery({
    queryKey: ['reference', 'setting-templates'],
    queryFn: async (): Promise<ReferenceItem[]> => {
      const res = await api.get('/setting-templates', {
        params: { includeDisabled: 'true', limit: 200 },
      })
      const rows = (res.data as { templateTypes?: TemplateRow[] }).templateTypes ?? []
      return rows.map((t) => ({
        id: t.templateTypeId,
        name: t.templateType,
        isDisabledByPlatform: t.isDisabledByPlatform,
      }))
    },
    staleTime: 10 * 60 * 1000,
  })
  return { templates: query.data ?? [], isLoading: query.isLoading }
}

interface TemplateRow {
  templateTypeId: number
  templateType: string
  isDisabledByPlatform: boolean
}

/**
 * useCurrencyOptions loads the currency catalogue as a picker list — the
 * counterpart of a rate is a currency, and 40 of them fit in one request.
 *
 * Retired currencies are included: the rate history goes back to 2019 and its
 * pairs must stay nameable, whatever the catalogue looks like today.
 */
export function useCurrencyOptions() {
  const query = useQuery({
    queryKey: ['reference', 'currencies'],
    queryFn: async (): Promise<ReferenceItem[]> => {
      const res = await api.get('/currencies', { params: { includeDisabled: 'true', limit: 200 } })
      const rows = (res.data as { currencies?: CurrencyRow[] }).currencies ?? []
      return rows.map((c) => ({
        id: c.currencyId,
        name: `${c.currencyCode} — ${c.currency}`,
        isDisabledByPlatform: c.isDisabledByPlatform,
      }))
    },
    staleTime: 10 * 60 * 1000,
  })
  return { currencies: query.data ?? [], isLoading: query.isLoading }
}

interface CurrencyRow {
  currencyId: number
  currency: string
  currencyCode: string
  isDisabledByPlatform: boolean
}
